import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ondit/ui";
import { Ellipsis } from "lucide-react";
import { logoutUrl } from "~/core/api";
import * as fmt from "~/core/format";
import { useI18n } from "~/core/i18n";
import { initials } from "~/shell/initials";

/**
 * The account, at the foot of the sidebar in both modules.
 *
 * One row rather than four. The foot of the column is where the eye goes last,
 * and four links there compete with the navigation above them for the same
 * glance — while the thing a person actually looks for down here is which
 * account they are in.
 *
 * The balance rides along on this row. It is the one number in the sidebar that
 * moves on its own, and this is now also the row that opens what it is spent
 * on.
 */
export function UserMenu({
	name,
	credits,
	collapsed = false,
	extra,
	onOpenSettings,
}: {
	name: string;
	/** Absent for a surface that has no account behind it yet. */
	credits?: number;
	collapsed?: boolean;
	/** Rows a module adds to the menu — the Agent's recently-deleted, say. */
	extra?: { label: string; onSelect: () => void }[];
	/** Absent for an account with no brand yet: the settings page lives under one. */
	onOpenSettings?: () => void;
}) {
	const { t, locale } = useI18n();
	return (
		<div className="flex items-center justify-between gap-2 px-2 pb-2">
			<Button
				variant="ghost"
				className="min-w-0 flex-1 gap-2 px-1.5 font-normal"
				aria-label={t("account.open")}
				onClick={onOpenSettings}
				disabled={!onOpenSettings}
			>
				<span
					aria-hidden
					className="flex size-(--s-9) shrink-0 items-center justify-center rounded-full bg-[var(--surface-active)] text-fg-2 text-label leading-none"
				>
					{initials(name || "?")}
				</span>
				{!collapsed && (
					<>
						<span className="min-w-0 flex-1 truncate text-left text-fg-2">{name}</span>
						{credits === undefined ? null : (
							<span className="shrink-0 tabular-nums text-[var(--fg-3)] text-label">
								{fmt.balance(locale, credits)}
							</span>
						)}
					</>
				)}
			</Button>
			{!collapsed && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="icon" aria-label={t("account.menu")}>
							<Ellipsis />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent side="top" align="end">
						{onOpenSettings ? <DropdownMenuItem onSelect={onOpenSettings}>{t("nav.settings")}</DropdownMenuItem> : null}
						{extra?.map((row) => (
							<DropdownMenuItem key={row.label} onSelect={row.onSelect}>
								{row.label}
							</DropdownMenuItem>
						))}
						<DropdownMenuSeparator />
						{/*
						 * A document navigation on purpose: signing out is the server's
						 * endpoint, and routing to it would render nothing while leaving
						 * the session open.
						 */}
						<DropdownMenuItem onSelect={() => window.location.assign(logoutUrl())}>{t("nav.signOut")}</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
}
